import { type ActionEntry, type SpecialActionTypeIds } from '@soulhx/fs-common';
import { useTranslation } from 'react-i18next';
import { type TFunction } from 'i18next';
import { db, useAsyncLiveQuery, useSpecialActionTypes } from '@soulhx/client-store';
import { DateTime } from 'luxon';

export interface GetEntriesResponse {
  entries: ActionEntry[] | undefined;
  entriesLoading: boolean;
}

/**
 * Options to refine how the data is processed
 */
interface GetAEOptions {
  /**
   * Whether long i18n names should be used (the default)
   */
  longNames?: boolean;
}

/**
 * Helper function to pre-process data from the "get action
 * entries" queries, to put the data in a more useable form
 * for the UI (translation).
 *
 * @param data Raw data
 * @param longNames Whether long or short names should be used
 * @param specialIds The Special IDs from the DB
 * @param t i18next translation function
 * @returns Data with i18n flavouring applied
 */
const getProcessedEntries = (
  data: ActionEntry[],
  longNames: boolean,
  specialIds: SpecialActionTypeIds,
  t: TFunction<'translation', undefined>
): ActionEntry[] => {
  const i18nPrefix = longNames === true ? 'do:actionTypes.long.' : 'do:actionTypes.short.';

  // Unresolved slots are '' (see useSpecialActionTypes) - skip them so they
  // can't all collide on the same '' key and shadow a real action type id.
  const suffixByActionTypeId = new Map<string, string>();
  for (const [suffix, id] of Object.entries(specialIds)) {
    if (id) suffixByActionTypeId.set(id, suffix);
  }

  const getDisplayName = (entry: ActionEntry): string => {
    const suffix = suffixByActionTypeId.get(entry.actionType);
    return suffix ? t(i18nPrefix + suffix) : entry.displayName;
  };

  const theArray: ActionEntry[] = data.map((ae) => ({
    id: ae.id,
    actionType: ae.actionType,
    completed: ae.completed,
    entryDate: ae.entryDate,
    displayName: getDisplayName(ae),
  }));

  return theArray;
};

export const useGetActionEntriesForMonth = (
  year: string,
  month: string,
  { longNames = true }: GetAEOptions = {}
): GetEntriesResponse => {
  const { data: specialIds, isLoading: specialTypesLoading } = useSpecialActionTypes();
  const { t } = useTranslation();
  const { data, isLoading } = useAsyncLiveQuery<ActionEntry[]>(async (): Promise<ActionEntry[]> => {
    if (specialTypesLoading) return [];

    const fromDate = DateTime.fromObject({ year: parseInt(year), month: parseInt(month) });
    const toDate = fromDate.endOf('month');

    const rawEntries = await db.actionEntries
      .where('entryDate')
      .between(fromDate.minus({ day: 1 }).toISODate()!, toDate.plus({ day: 1 }).toISODate()!, true, true)
      .and((ae) => {
        // rows queued for deletion are treated as already gone
        if (ae.syncStatus === 'pendingDelete') return false;
        const entryDate = DateTime.fromISO(ae.entryDate);
        return entryDate >= fromDate && entryDate <= toDate;
      })
      .toArray();

    const typeIds = [...new Set(rawEntries.map((e) => e.actionType))];
    const types = await db.actionTypes.where('id').anyOf(typeIds).toArray();
    const typesById = new Map(types.map((t) => [t.id, t]));

    const entries: ActionEntry[] = rawEntries.map((re) => ({
      ...re,
      displayName: typesById.get(re.actionType)!.displayName,
    }));

    return getProcessedEntries(entries, longNames, specialIds, t);
  }, [longNames, specialIds, t, specialTypesLoading, year, month]);

  return { entries: data ?? undefined, entriesLoading: isLoading || specialTypesLoading };
};

export const useGetActionEntriesForDay = (
  year: string,
  month: string,
  day: string,
  { longNames = true }: GetAEOptions = {}
): GetEntriesResponse => {
  const { data: specialIds, isLoading: specialTypesLoading } = useSpecialActionTypes();
  const { t } = useTranslation();
  const { data, isLoading } = useAsyncLiveQuery<ActionEntry[]>(async (): Promise<ActionEntry[]> => {
    if (specialTypesLoading) return [];

    const fromDate = DateTime.fromObject({ year: parseInt(year), month: parseInt(month), day: parseInt(day) });
    const toDate = fromDate.endOf('day');

    const rawEntries = await db.actionEntries
      .where('entryDate')
      .between(fromDate.minus({ day: 1 }).toISODate()!, toDate.plus({ day: 1 }).toISODate()!, true, true)
      .and((ae) => {
        if (ae.syncStatus === 'pendingDelete') return false;
        const entryDate = DateTime.fromISO(ae.entryDate);
        return entryDate >= fromDate && entryDate <= toDate;
      })
      .toArray();

    const typeIds = [...new Set(rawEntries.map((e) => e.actionType))];
    const types = await db.actionTypes.where('id').anyOf(typeIds).toArray();
    const typesById = new Map(types.map((t) => [t.id, t]));

    const entries: ActionEntry[] = rawEntries.map((re) => ({
      ...re,
      displayName: typesById.get(re.actionType)?.displayName ?? '',
    }));

    return getProcessedEntries(entries, longNames, specialIds, t);
  }, [specialTypesLoading, year, month, day, longNames, specialIds, t]);

  return { entries: data ?? undefined, entriesLoading: isLoading || specialTypesLoading };
};
